import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { showAPI, resultsAPI, sidepotAPI, averageAPI, targetAPI } from '../utils/api';
import { CalendarBlank, MapPin, Trophy, CurrencyDollar, Printer, DownloadSimple, EnvelopeSimple, X, ArrowsClockwise, ChartLine, Target } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../utils/api';
import { getDivisionColors } from '../utils/divisionColors';

const ShowResults = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [show, setShow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSidepot, setSelectedSidepot] = useState(null);
  const [results, setResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overall');
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailCustomText, setEmailCustomText] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [payoutInfo, setPayoutInfo] = useState(null);
  const [showTotalEarnings, setShowTotalEarnings] = useState(false);
  const [totalEarnings, setTotalEarnings] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [expandedRider, setExpandedRider] = useState(null);
  // v262 — Averages + Target Races are now viewable on the public
  // results page alongside classes & sidepots.
  const [averages, setAverages] = useState([]);
  const [targets, setTargets] = useState([]);
  const [selectedAverage, setSelectedAverage] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [avgResults, setAvgResults] = useState(null);
  const [tgtResults, setTgtResults] = useState(null);
  const [avgResultsLoading, setAvgResultsLoading] = useState(false);
  const [tgtResultsLoading, setTgtResultsLoading] = useState(false);

  useEffect(() => {
    loadShow();
  }, [id]);

  const loadShow = async () => {
    try {
      const { data } = await showAPI.get(id);
      setShow(data);
      // v262 — Fetch averages + target races so they appear on the
      // results page sidebar. Failures are non-fatal (a legacy show
      // with none configured just shows an empty section).
      try {
        const { data: avgList } = await averageAPI.list(id);
        setAverages(Array.isArray(avgList) ? avgList : []);
      } catch { setAverages([]); }
      try {
        const { data: tgtList } = await targetAPI.list(id);
        setTargets(Array.isArray(tgtList) ? tgtList : []);
      } catch { setTargets([]); }
      if (data.classes.length > 0) {
        handleSelectClass(data.classes[0]);
      }
    } catch {
      console.error('Failed to load show');
    } finally {
      setLoading(false);
    }
  };

  // v262 — Selecting an average or target race clears class/sidepot
  // selections and loads results from the public results endpoints.
  const handleSelectAverage = async (avg) => {
    setSelectedClass(null);
    setSelectedSidepot(null);
    setSelectedTarget(null);
    setShowTotalEarnings(false);
    setSelectedAverage(avg);
    setAvgResults(null);
    setAvgResultsLoading(true);
    try {
      const { data } = await averageAPI.results(id, avg.id);
      setAvgResults(data);
    } catch {
      setAvgResults(null);
    } finally {
      setAvgResultsLoading(false);
    }
  };

  const handleSelectTarget = async (tgt) => {
    setSelectedClass(null);
    setSelectedSidepot(null);
    setSelectedAverage(null);
    setShowTotalEarnings(false);
    setSelectedTarget(tgt);
    setTgtResults(null);
    setTgtResultsLoading(true);
    try {
      const { data } = await targetAPI.results(id, tgt.id);
      setTgtResults(data);
    } catch {
      setTgtResults(null);
    } finally {
      setTgtResultsLoading(false);
    }
  };

  const handleSelectClass = async (cls) => {
    setSelectedClass(cls);
    setSelectedSidepot(null);
    setSelectedAverage(null);
    setSelectedTarget(null);
    setShowTotalEarnings(false);
    setResultsLoading(true);
    setActiveTab('overall');
    try {
      const { data } = await resultsAPI.get(cls.id);
      setResults(data);
      setPayoutInfo(data.payouts || null);
    } catch {
      setResults(null);
    } finally {
      setResultsLoading(false);
    }
  };

  // Auto-refresh class standings every 10s while a class is selected on
  // the public results page — contestants and announcers see new times +
  // re-rankings appear live without needing to refresh the browser.
  // Guarded by `__isSidepot` so the sidepot polling effect below handles
  // sidepot views (different endpoint + reshape).
  useEffect(() => {
    if (!selectedClass?.id || selectedClass.__isSidepot) return undefined;
    const cls = selectedClass;
    const tick = async () => {
      try {
        const { data } = await resultsAPI.get(cls.id);
        // Late-response guard — bail if the user has switched classes.
        setResults(prev => (selectedClass?.id === cls.id ? data : prev));
        setPayoutInfo(prev => (selectedClass?.id === cls.id ? (data.payouts || null) : prev));
      } catch { /* transient — keep last good */ }
    };
    const handle = setInterval(tick, 10000);
    return () => clearInterval(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass?.id, selectedClass?.__isSidepot]);

  // Reshape raw sidepot results into the class-results shape consumed by
  // the right-pane renderer. Extracted so we can call it on initial
  // click AND from the auto-refresh polling timer below.
  const reshapeSidepot = (data, sp) => {
    const divType = data.division_type || sp.division_type || 'NONE';
    const divisionStandings = {};
    // Lookup payout $ per (division, place) so each rider row can display
    // their division payout next to their name without re-doing the
    // matrix at render time.
    const divPayoutLookup = {};
    Object.entries(data.division_payouts || {}).forEach(([divName, dd]) => {
      (dd.payouts || []).forEach(p => {
        divPayoutLookup[`${divName}::${p.place}`] = p.amount;
      });
    });

    if (divType !== 'NONE') {
      (data.results || []).forEach(r => {
        const div = r.division;
        if (!div) return;
        const place = r.division_place || r.place;
        (divisionStandings[div] ||= []).push({
          ...r,
          place,
          division_payout_amount: divPayoutLookup[`${div}::${place}`] || 0,
        });
      });
      Object.keys(divisionStandings).forEach(div => {
        divisionStandings[div].sort((a, b) => (a.place || 999) - (b.place || 999));
      });
    }
    return {
      results: (data.results || []).map(r => {
        const div = r.division;
        const place = r.division_place || r.place;
        return {
          ...r,
          status: r.final_time != null ? 'COMPLETED' : 'NT',
          knocks: r.knocks || 0,
          // Surfaced so the Overall tab can show "1D · 1st · $191" right
          // next to a rider's name even before division tabs are clicked.
          division: div,
          division_place: r.division_place,
          division_payout_amount: div ? (divPayoutLookup[`${div}::${place}`] || 0) : 0,
        };
      }),
      entrants: data.entrants || [],
      division_standings: divisionStandings,
      division_type: divType,
      payouts: {
        total_pool: data.total_pool || 0,
        payouts: data.payouts || [],
        division_payouts: data.division_payouts || {},
      },
    };
  };

  // Reshape a sidepot results payload into the class-results shape consumed
  // by the right-pane renderer (results / division_standings / division_type
  // / payouts). This lets us reuse all the existing rendering, exporting,
  // and division-tab logic without duplication.
  const handleSelectSidepot = async (sp) => {
    // Synthetic selectedClass so the right pane treats this like a class.
    const synthetic = {
      id: sp.id,
      name: sp.name,
      division_type: sp.division_type || 'NONE',
      entry_fee: sp.entry_fee || 0,
      __isSidepot: true,
    };
    setSelectedClass(synthetic);
    setSelectedSidepot(sp);
    setSelectedAverage(null);
    setSelectedTarget(null);
    setShowTotalEarnings(false);
    setResultsLoading(true);
    setActiveTab('overall');
    try {
      const { data } = await sidepotAPI.results(id, sp.id);
      const reshaped = reshapeSidepot(data, sp);
      setResults(reshaped);
      setPayoutInfo(reshaped.payouts);
    } catch {
      setResults(null);
      setPayoutInfo(null);
    } finally {
      setResultsLoading(false);
    }
  };

  // Auto-refresh sidepot standings every 10s while a sidepot is selected
  // on the public results page so contestants see new placings appear
  // live as the scorekeeper enters times. Polling pauses if the user
  // navigates to a class or to total-earnings.
  useEffect(() => {
    if (!selectedSidepot?.id) return undefined;
    const sp = selectedSidepot;
    const tick = async () => {
      try {
        const { data } = await sidepotAPI.results(id, sp.id);
        const reshaped = reshapeSidepot(data, sp);
        // Guard: only commit if the user is still viewing the same sidepot.
        setResults(prev => (selectedSidepot?.id === sp.id ? reshaped : prev));
        setPayoutInfo(prev => (selectedSidepot?.id === sp.id ? reshaped.payouts : prev));
      } catch { /* transient — keep last good state */ }
    };
    const handle = setInterval(tick, 10000);
    return () => clearInterval(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSidepot?.id, id]);

  const handleShowTotalEarnings = async () => {
    setShowTotalEarnings(true);
    setSelectedClass(null);
    setSelectedSidepot(null);
    setSelectedAverage(null);
    setSelectedTarget(null);
    setExpandedRider(null);
    setEarningsLoading(true);
    try {
      const { data } = await api.get(`/shows/${id}/total-earnings`);
      setTotalEarnings(data);
    } catch {
      setTotalEarnings(null);
    } finally {
      setEarningsLoading(false);
    }
  };

  const divisionStandings = results?.division_standings || {};
  const divisionType = results?.division_type || 'NONE';
  const divisionNames = Object.keys(divisionStandings).sort();
  const hasDivisions = divisionType !== 'NONE' && divisionNames.length > 0;
  const displayResults = activeTab === 'overall' ? (results?.results || []) : (divisionStandings[activeTab] || []);
  // Each overall result already carries its `division` field from the backend.
  // Fallback: look it up by rider+horse in the division_standings map.
  // Also track each rider's in-division place so the Overall tab can show
  // the same payout they'd see on their division's tab.
  const { entryDivisionMap, entryDivisionPlaceMap } = React.useMemo(() => {
    const divMap = {};
    const placeMap = {};
    Object.entries(divisionStandings || {}).forEach(([divName, arr]) => {
      (arr || []).forEach(r => {
        const key = `${r.rider_name || ''}||${r.horse_name || ''}`;
        if (key && !divMap[key]) divMap[key] = divName;
        if (key && r.place && !placeMap[key]) placeMap[key] = r.place;
      });
    });
    return { entryDivisionMap: divMap, entryDivisionPlaceMap: placeMap };
  }, [divisionStandings]);
  const getDivisionFor = (r) => r.division || entryDivisionMap[`${r.rider_name || ''}||${r.horse_name || ''}`];
  const getDivisionPlaceFor = (r) => entryDivisionPlaceMap[`${r.rider_name || ''}||${r.horse_name || ''}`];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="shimmer h-12 w-12 rounded-full"></div>
      </div>
    );
  }

  if (!show) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <p className="text-[#9E9E9E]">Show not found</p>
      </div>
    );
  }

  const getPayoutForPlace = (place, divName) => {
    if (!payoutInfo) return null;
    if (divName && divName !== 'overall' && payoutInfo.division_payouts?.[divName]) {
      const divPayouts = payoutInfo.division_payouts[divName].payouts;
      if (Array.isArray(divPayouts)) {
        const p = divPayouts.find(p => p.place === place);
        return p ? p.amount : null;
      }
      return divPayouts?.[String(place)] || null;
    }
    const overall = payoutInfo.overall_payouts || payoutInfo.payouts;
    if (Array.isArray(overall)) {
      const p = overall.find(p => p.place === place);
      return p ? p.amount : null;
    }
    if (overall && typeof overall === 'object') {
      return overall[String(place)] || overall[place] || null;
    }
    return null;
  };

  // === Export/Print ===
  // Division color palette — 1D light blue, 2D pink, 3D yellow, 4D green.
  const DIVISION_COLORS = { '1D': '#D6EAF8', '2D': '#F9D6E2', '3D': '#FCF3CF', '4D': '#D4EFDF' };
  const divisionColor = (name) => {
    if (!name) return null;
    const key = String(name).trim().toUpperCase();
    return Object.keys(DIVISION_COLORS).find(k => key.startsWith(k)) ? DIVISION_COLORS[Object.keys(DIVISION_COLORS).find(k => key.startsWith(k))] : null;
  };

  // Build a flat list with division info; used by both CSV and print.
  // NT / SCR entries are included with their status.
  // On Overall, each rider's payout is looked up from their DIVISION (in-division
  // place) so the overall export shows the same $ amounts as the division tabs.
  const buildRowsForExport = () => {
    return displayResults.map((r, i) => {
      const isNT = r.status === 'NT' || r.final_time === null;
      const isSCR = r.status === 'SCR';
      const place = (isNT || isSCR) ? '' : (r.place || i + 1);
      const divName = getDivisionFor(r);
      const divPlace = getDivisionPlaceFor(r);
      let payout = null;
      if (place) {
        if (activeTab === 'overall') {
          if (divName && divPlace) payout = getPayoutForPlace(divPlace, divName);
        } else {
          payout = getPayoutForPlace(place, activeTab);
        }
      }
      // On overall, display "DIV-PLACE" (e.g. "2D-1"); otherwise keep the raw place.
      const placeDisplay = (activeTab === 'overall' && divName && divPlace)
        ? `${divName}-${divPlace}`
        : (place ? String(place) : '');
      return {
        place,
        placeDisplay,
        rider: r.rider_name || '',
        horse: r.horse_name || '',
        time: isNT ? 'NT' : isSCR ? 'SCR' : (r.final_time?.toFixed(3) ?? ''),
        status: r.status || '',
        payout: payout ? `$${Number(payout).toFixed(0)}` : '',
        division: divName || '',
        divPlace: divPlace || '',
        isNT, isSCR,
      };
    });
  };

  const exportCSV = async () => {
    // Downloads a real Excel (.xlsx) file with division color fills — Numbers,
    // Excel, Google Sheets, and mobile spreadsheet apps all render the colors.
    if (!selectedClass || displayResults.length === 0) return;
    try {
      // Sidepots have their own xlsx endpoint; classes use /results/{id}/xlsx.
      const path = selectedSidepot
        ? `/sidepot-results/${selectedSidepot.id}/xlsx`
        : `/results/${selectedClass.id}/xlsx?division=${encodeURIComponent(activeTab)}`;
      const resp = await api.get(path, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const tabSuffix = activeTab === 'overall' ? '' : `_${activeTab}`;
      const baseName = selectedSidepot ? `sidepot_${selectedSidepot.name}` : selectedClass.name;
      const name = `${show.name.replace(/[^a-z0-9]+/gi, '_')}_${baseName.replace(/[^a-z0-9]+/gi, '_')}${tabSuffix}_results.xlsx`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      toast.error('Failed to export Excel file');
    }
  };

  const exportCombinedXlsx = async () => {
    // Single workbook with one tab per class + per sidepot. Skips classes
    // without published/calculated results so the file isn't padded with
    // empty placeholder tabs.
    if (!show) return;
    try {
      const resp = await api.get(`/shows/${id}/combined-results.xlsx`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const name = `${show.name.replace(/[^a-z0-9]+/gi, '_')}_all_results.xlsx`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Combined results downloaded');
    } catch (e) {
      toast.error('Failed to export combined results');
    }
  };

  const printResults = () => {
    if (!selectedClass || displayResults.length === 0) return;
    const title = `${show.name} — ${selectedClass.name}${activeTab !== 'overall' ? ` (${activeTab})` : ''}`;
    const rows = buildRowsForExport();
    const isOverallWithDivs = activeTab === 'overall' && hasDivisions;
    const isOverall = activeTab === 'overall';
    // Include Payout column on overall too — payouts come from each rider's division.
    const colCount = 5;

    const renderRow = (r, bg) => `<tr${bg ? ` style="background:${bg} !important;-webkit-print-color-adjust:exact;print-color-adjust:exact;"` : ''}>
        <td style="text-align:center;font-weight:700">${r.isSCR ? 'SCR' : r.isNT ? 'NT' : r.placeDisplay}</td>
        <td>${(r.rider || '').replace(/</g, '&lt;')}</td>
        <td>${(r.horse || '').replace(/</g, '&lt;')}</td>
        <td style="text-align:right;font-family:monospace">${r.time}</td>
        <td style="text-align:right;font-family:monospace;color:#1e8e3e;font-weight:700">${r.payout || ''}</td>
      </tr>`;

    let bodyHtml = '';
    if (isOverallWithDivs) {
      const byDiv = {};
      const unplaced = [];
      rows.forEach(r => {
        if (r.isNT || r.isSCR || !r.division) unplaced.push(r);
        else (byDiv[r.division] ||= []).push(r);
      });
      const divNames = Object.keys(byDiv).sort();
      divNames.forEach(divName => {
        const divRows = byDiv[divName].slice().sort((a, b) => (a.place || 999) - (b.place || 999));
        const color = divisionColor(divName);
        const headerBg = color || '#0A0A0A';
        const headerFg = color ? '#111' : '#fff';
        bodyHtml += `<tr class="divhead"><td colspan="${colCount}" style="background:${headerBg} !important;color:${headerFg};padding:8px 10px;font-weight:700;border-top:1px solid rgba(0,0,0,0.08);-webkit-print-color-adjust:exact;print-color-adjust:exact;">${divName}</td></tr>`;
        bodyHtml += divRows.map(r => renderRow(r, color)).join('');
      });
      if (unplaced.length) {
        bodyHtml += `<tr class="divhead"><td colspan="${colCount}" style="background:#E5E7EB !important;color:#111;padding:8px 10px;font-weight:700;-webkit-print-color-adjust:exact;print-color-adjust:exact;">No Time / Scratched</td></tr>`;
        bodyHtml += unplaced.map(r => renderRow(r)).join('');
      }
    } else {
      const color = !isOverall ? divisionColor(activeTab) : null;
      bodyHtml = rows.map(r => {
        if (r.isNT || r.isSCR) return renderRow(r);
        return renderRow(r, color);
      }).join('');
    }

    const headerRow = `<tr><th style="width:50px;text-align:center">Place</th><th>Rider</th><th>Horse</th><th style="text-align:right">Time</th><th style="text-align:right">Payout</th></tr>`;

    const html = `<!doctype html><html><head><title>${title}</title>
      <meta charset="utf-8" />
      <style>
        *{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;color-adjust:exact !important}
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#111;padding:24px;margin:0}
        h1{margin:0 0 4px;font-size:20px}
        .sub{color:#666;font-size:12px;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th{text-align:left;padding:6px 8px;background:#f3f3f3;border-bottom:1px solid #ccc}
        td{padding:6px 8px;border-bottom:1px solid #eee}
        .footer{margin-top:20px;color:#888;font-size:10px}
        @media print{@page{margin:0.4in}}
      </style></head>
      <body>
        <h1>${title.replace(/</g, '&lt;')}</h1>
        <div class="sub">${show.start_date || ''}${show.end_date && show.end_date !== show.start_date ? ' — ' + show.end_date : ''} ${show.location ? '· ' + show.location : ''}</div>
        <table>
          <thead>${headerRow}</thead>
          <tbody>${bodyHtml}</tbody>
        </table>
        <div class="footer">Generated ${new Date().toLocaleString()} · The Show Master</div>
        <script>window.onload = () => { window.print(); };</script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  const sendEmailResults = async () => {
    if (!selectedClass) return;
    const recipients = emailCustomText
      .split(/[,\n;]/)
      .map(e => e.trim())
      .filter(e => e && /\S+@\S+\.\S+/.test(e));
    if (recipients.length === 0) {
      toast.error('Enter at least one valid email address');
      return;
    }
    setEmailSending(true);
    try {
      const path = selectedSidepot
        ? `/sidepot-results/${selectedSidepot.id}/email`
        : `/results/${selectedClass.id}/email`;
      const { data } = await api.post(path, {
        mode: 'custom',
        recipients,
        division: activeTab,
        include_csv: true,
        include_pdf: true,
      });
      toast.success(`Sent to ${data.sent} of ${data.total_recipients} recipient${data.total_recipients !== 1 ? 's' : ''}`);
      setEmailModalOpen(false);
      setEmailCustomText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };


  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0096FF]/10 to-[#00D4FF]/5 border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-2" style={{fontFamily: 'Outfit'}} data-testid="results-show-name">{show.name}</h1>
          <div className="flex flex-wrap gap-4 text-[#9E9E9E] text-sm">
            <span className="flex items-center gap-1.5"><CalendarBlank size={14} /> {new Date(show.start_date).toLocaleDateString()} - {new Date(show.end_date).toLocaleDateString()}</span>
            <span className="flex items-center gap-1.5"><MapPin size={14} /> {show.location}</span>
            <span className={`px-2.5 py-0.5 rounded text-xs font-bold ${
              show.state === 'LIVE' ? 'bg-[#FF3B30]/15 text-[#FF3B30]' :
              show.state === 'COMPLETED' ? 'bg-[#34C759]/15 text-[#34C759]' :
              'bg-[#0096FF]/15 text-[#0096FF]'
            }`}>{show.state}</span>
          </div>
          <div className="mt-4">
            <button
              onClick={exportCombinedXlsx}
              data-testid="export-combined-xlsx-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#34C759]/10 border border-[#34C759]/30 hover:bg-[#34C759]/20 text-[#34C759] font-semibold rounded-md transition-colors text-sm"
              title="Download a single Excel workbook with one tab per class & sidepot"
            >
              <DownloadSimple size={14} weight="bold" /> Download All Results (Excel)
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Class Selector */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-bold text-white mb-3" style={{fontFamily: 'Outfit'}}>Classes</h3>
            {/* Total Money Earned Button */}
            <button
              onClick={handleShowTotalEarnings}
              data-testid="total-earnings-btn"
              className={`w-full text-left p-3 rounded-lg border transition-all mb-3 ${
                showTotalEarnings
                  ? 'bg-[#34C759]/10 border-[#34C759] text-white'
                  : 'bg-[#161616] border-[#262626] text-[#9E9E9E] hover:border-[#34C759]/50 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <CurrencyDollar size={18} weight="bold" className={showTotalEarnings ? 'text-[#34C759]' : 'text-[#9E9E9E]'} />
                <div>
                  <p className="font-semibold text-sm">Total Money Earned</p>
                  <p className="text-xs opacity-70">All classes combined</p>
                </div>
              </div>
            </button>
            {(() => {
              const isSingleDay = show.start_date === show.end_date;
              const dayGroups = {};
              // Hide warm-up / exhibition classes from the results sidebar —
              // they're for practice and don't have payouts or placings.
              const resultsClasses = (show.classes || []).filter(c => !['warmup','exhibition'].includes(c.class_type));
              resultsClasses.forEach(cls => {
                const day = (!isSingleDay && cls.scheduled_day) ? cls.scheduled_day : 'All';
                if (!dayGroups[day]) dayGroups[day] = [];
                dayGroups[day].push(cls);
              });
              const dayKeys = Object.keys(dayGroups);
              const isMultiDay = dayKeys.length > 1 || (dayKeys.length === 1 && dayKeys[0] !== 'All');
              return (
                <div className="space-y-2">
                  {isMultiDay ? dayKeys.map(day => (
                    <div key={day}>
                      <p className="text-xs font-bold text-[#0096FF] uppercase tracking-wider mb-1.5 mt-3">{day}</p>
                      {dayGroups[day].map(cls => (
                        <button key={cls.id} onClick={() => handleSelectClass(cls)} data-testid={`results-class-${cls.id}`}
                          className={`w-full text-left p-3 rounded-lg border transition-all mb-1.5 ${
                            selectedClass?.id === cls.id ? 'bg-[#0096FF]/10 border-[#0096FF] text-white' : 'bg-[#161616] border-[#262626] text-[#9E9E9E] hover:border-[#0096FF]/50 hover:text-white'
                          }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-sm">{cls.name}</p>
                              <p className="text-xs opacity-70 mt-0.5">{cls.division_type && cls.division_type !== 'NONE' && `${cls.division_type} • `}${cls.entry_fee} entry</p>
                            </div>
                            {cls.status === 'COMPLETED' && <span className="w-2 h-2 rounded-full bg-[#34C759] flex-shrink-0"></span>}
                            {cls.status === 'LIVE' && <span className="relative flex h-2 w-2 flex-shrink-0"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF3B30]"></span></span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )) : resultsClasses.map(cls => (
                    <button key={cls.id} onClick={() => handleSelectClass(cls)} data-testid={`results-class-${cls.id}`}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        selectedClass?.id === cls.id ? 'bg-[#0096FF]/10 border-[#0096FF] text-white' : 'bg-[#161616] border-[#262626] text-[#9E9E9E] hover:border-[#0096FF]/50 hover:text-white'
                      }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-sm">{cls.name}</p>
                          <p className="text-xs opacity-70 mt-0.5">{cls.division_type && cls.division_type !== 'NONE' && `${cls.division_type} • `}${cls.entry_fee} entry</p>
                        </div>
                        {cls.status === 'COMPLETED' && <span className="w-2 h-2 rounded-full bg-[#34C759] flex-shrink-0"></span>}
                        {cls.status === 'LIVE' && <span className="relative flex h-2 w-2 flex-shrink-0"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF3B30]"></span></span>}
                      </div>
                    </button>
                  ))}
                </div>
              );
            })()}

            {/* Sidepots — listed below classes; clicking renders the same
                results UI by reshaping sidepot results into class-shape. */}
            {(show.sidepots || []).length > 0 && (
              <div className="mt-5">
                <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Outfit' }}>Sidepots</h3>
                <div className="space-y-2">
                  {(show.sidepots || []).map(sp => (
                    <button
                      key={sp.id}
                      onClick={() => handleSelectSidepot(sp)}
                      data-testid={`results-sidepot-${sp.id}`}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        selectedSidepot?.id === sp.id
                          ? 'bg-[#FACC15]/10 border-[#FACC15] text-white'
                          : 'bg-[#161616] border-[#262626] text-[#9E9E9E] hover:border-[#FACC15]/50 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-sm">{sp.name}</p>
                          <p className="text-xs opacity-70 mt-0.5">
                            {sp.division_type && sp.division_type !== 'NONE' && `${sp.division_type} • `}${sp.entry_fee} entry
                          </p>
                        </div>
                        <span className="px-1.5 py-0.5 bg-[#FACC15]/15 text-[#FACC15] rounded text-[9px] font-bold flex-shrink-0">SIDEPOT</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* v262 — Averages section on the public results page. */}
            {averages.length > 0 && (
              <div className="mt-5" data-testid="results-averages-section">
                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <ChartLine size={18} weight="bold" className="text-[#AF52DE]" /> Averages
                </h3>
                <div className="space-y-2">
                  {averages.map(avg => (
                    <button
                      key={avg.id}
                      onClick={() => handleSelectAverage(avg)}
                      data-testid={`results-average-${avg.id}`}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        selectedAverage?.id === avg.id
                          ? 'bg-[#AF52DE]/10 border-[#AF52DE] text-white'
                          : 'bg-[#161616] border-[#262626] text-[#9E9E9E] hover:border-[#AF52DE]/50 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-sm">{avg.name}</p>
                          <p className="text-xs opacity-70 mt-0.5">
                            {(avg.selected_class_ids || []).length} classes
                            {avg.division_type && avg.division_type !== 'NONE' && ` • ${avg.division_type}`}
                          </p>
                        </div>
                        <span className="px-1.5 py-0.5 bg-[#AF52DE]/15 text-[#AF52DE] rounded text-[9px] font-bold flex-shrink-0">AVG</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* v262 — Target Races section on the public results page. */}
            {targets.length > 0 && (
              <div className="mt-5" data-testid="results-targets-section">
                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <Target size={18} weight="bold" className="text-[#0096FF]" /> Target Races
                </h3>
                <div className="space-y-2">
                  {targets.map(tgt => (
                    <button
                      key={tgt.id}
                      onClick={() => handleSelectTarget(tgt)}
                      data-testid={`results-target-${tgt.id}`}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        selectedTarget?.id === tgt.id
                          ? 'bg-[#0096FF]/10 border-[#0096FF] text-white'
                          : 'bg-[#161616] border-[#262626] text-[#9E9E9E] hover:border-[#0096FF]/50 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-sm">{tgt.name}</p>
                          <p className="text-xs opacity-70 mt-0.5">
                            {tgt.payout_type === 'PRIZES_ONLY' ? 'Prizes only' : `$${Number(tgt.entry_fee || 0).toFixed(0)} opt-in`}
                          </p>
                        </div>
                        <span className="px-1.5 py-0.5 bg-[#0096FF]/15 text-[#0096FF] rounded text-[9px] font-bold flex-shrink-0">TARGET</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Results */}
          <div className="lg:col-span-3">
            {showTotalEarnings ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-white" style={{fontFamily: 'Outfit'}} data-testid="total-earnings-title">
                    Total Money Earned
                  </h2>
                  {totalEarnings && (
                    <span className="text-[#9E9E9E] text-sm">{totalEarnings.total_classes} class{totalEarnings.total_classes !== 1 ? 'es' : ''} scored</span>
                  )}
                </div>
                {earningsLoading ? (
                  <div className="p-12 text-center"><div className="shimmer h-8 w-8 rounded-full mx-auto"></div></div>
                ) : !totalEarnings || totalEarnings.earnings.length === 0 ? (
                  <div className="bg-[#161616] border border-[#262626] rounded-lg p-12 text-center">
                    <CurrencyDollar size={40} className="text-[#262626] mx-auto mb-3" />
                    <p className="text-[#9E9E9E]">No published results with payouts yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {totalEarnings.earnings.map((rider, index) => (
                      <div key={rider.rider_name}>
                        <button
                          onClick={() => setExpandedRider(expandedRider === rider.rider_name ? null : rider.rider_name)}
                          data-testid={`earnings-row-${index}`}
                          className={`w-full bg-[#161616] border rounded-lg p-4 sm:p-5 text-left transition-all ${
                            index === 0 ? 'border-[#FFD700] bg-gradient-to-r from-[#FFD700]/5 to-transparent' :
                            index === 1 ? 'border-[#C0C0C0] bg-gradient-to-r from-[#C0C0C0]/5 to-transparent' :
                            index === 2 ? 'border-[#CD7F32] bg-gradient-to-r from-[#CD7F32]/5 to-transparent' :
                            'border-[#262626] hover:border-[#333]'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                              <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                index === 0 ? 'bg-[#FFD700]/20' :
                                index === 1 ? 'bg-[#C0C0C0]/20' :
                                index === 2 ? 'bg-[#CD7F32]/20' :
                                'bg-[#0A0A0A]'
                              }`}>
                                <span className={`text-sm sm:text-base font-black ${
                                  index === 0 ? 'text-[#FFD700]' :
                                  index === 1 ? 'text-[#C0C0C0]' :
                                  index === 2 ? 'text-[#CD7F32]' :
                                  'text-[#9E9E9E]'
                                }`} style={{fontFamily: 'Outfit'}}>{index + 1}</span>
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm sm:text-base font-bold text-white truncate" style={{fontFamily: 'Outfit'}}>{rider.rider_name}</p>
                                <p className="text-[#9E9E9E] text-[10px] sm:text-xs">
                                  {rider.classes.length} class{rider.classes.length !== 1 ? 'es' : ''} paid
                                </p>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-lg sm:text-xl font-black text-[#34C759]" style={{fontFamily: 'JetBrains Mono'}} data-testid={`earnings-amount-${index}`}>
                                ${rider.total.toLocaleString(undefined, {minimumFractionDigits: 2})}
                              </p>
                              <p className="text-[#9E9E9E] text-[10px]">{expandedRider === rider.rider_name ? 'tap to collapse' : 'tap for details'}</p>
                            </div>
                          </div>
                        </button>
                        {expandedRider === rider.rider_name && (
                          <div className="ml-11 mt-1 mb-1 space-y-1" data-testid={`earnings-detail-${index}`}>
                            {rider.classes.map((cls, ci) => (
                              <div key={ci} className="flex items-center justify-between bg-[#0A0A0A] border border-[#1A1A1A] rounded-md px-3 py-2">
                                <span className="text-[#9E9E9E] text-xs truncate mr-2">{cls.class_name}</span>
                                <span className="text-[#34C759] text-xs font-bold whitespace-nowrap">${cls.amount.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : selectedAverage ? (
              <AverageResultsView
                showId={id}
                showName={show.name}
                avg={selectedAverage}
                results={avgResults}
                loading={avgResultsLoading}
                onClose={() => { setSelectedAverage(null); setAvgResults(null); }}
              />
            ) : selectedTarget ? (
              <TargetResultsView
                showId={id}
                showName={show.name}
                target={selectedTarget}
                results={tgtResults}
                loading={tgtResultsLoading}
                onClose={() => { setSelectedTarget(null); setTgtResults(null); }}
              />
            ) : selectedClass && (
              <>
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <h2 className="text-2xl font-bold text-white" style={{fontFamily: 'Outfit'}} data-testid="results-class-name">
                    {selectedClass.name}
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={exportCSV}
                      disabled={displayResults.length === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-xs font-semibold hover:text-white hover:border-[#34C759]/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="results-export-csv"
                      title="Download color-coded Excel (.xlsx)"
                    >
                      <DownloadSimple size={14} weight="bold" /> Export Excel
                    </button>
                    <button
                      onClick={printResults}
                      disabled={displayResults.length === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-xs font-semibold hover:text-white hover:border-[#0096FF]/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="results-print"
                      title="Print or save as PDF"
                    >
                      <Printer size={14} weight="bold" /> Print / PDF
                    </button>
                    <button
                      onClick={() => setEmailModalOpen(true)}
                      disabled={displayResults.length === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-xs font-semibold hover:text-white hover:border-[#FF9500]/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="results-email-btn"
                      title="Email results to yourself, riders, or custom recipients"
                    >
                      <EnvelopeSimple size={14} weight="bold" /> Email
                    </button>
                    <div className="text-right ml-1">
                      <span className="text-[#9E9E9E] text-sm">{(results?.results || []).length} competitors</span>
                      {payoutInfo && payoutInfo.total_pool > 0 && (
                        <p className="text-[#34C759] text-sm font-bold" data-testid="results-pool-amount">
                          Pool: ${payoutInfo.total_pool.toLocaleString()}
                          {activeTab !== 'overall' && payoutInfo.division_payouts?.[activeTab] && (
                            <span className="text-[#9E9E9E] font-normal"> (Div: ${payoutInfo.division_payouts[activeTab].pool.toLocaleString()})</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Division Tabs */}
                {hasDivisions && (
                  <div className="flex gap-1 mb-4 overflow-x-auto pb-1" data-testid="results-division-tabs">
                    <button
                      onClick={() => setActiveTab('overall')}
                      data-testid="results-tab-overall"
                      className={`px-4 py-2 rounded-md text-sm font-semibold whitespace-nowrap transition-colors ${
                        activeTab === 'overall' ? 'bg-[#0096FF] text-white' : 'bg-[#161616] border border-[#262626] text-[#9E9E9E] hover:text-white'
                      }`}
                    >
                      Overall
                    </button>
                    {divisionNames.map((divName) => (
                      <button
                        key={divName}
                        onClick={() => setActiveTab(divName)}
                        data-testid={`results-tab-${divName.toLowerCase()}`}
                        className={`px-4 py-2 rounded-md text-sm font-semibold whitespace-nowrap transition-colors ${
                          activeTab === divName ? 'bg-[#0096FF] text-white' : 'bg-[#161616] border border-[#262626] text-[#9E9E9E] hover:text-white'
                        }`}
                      >
                        {divName}
                      </button>
                    ))}
                  </div>
                )}

                {resultsLoading ? (
                  <div className="p-12 text-center"><div className="shimmer h-8 w-8 rounded-full mx-auto"></div></div>
                ) : displayResults.length === 0 ? (
                  selectedClass?.__isSidepot && (results?.entrants || []).length > 0 ? (
                    <div className="bg-[#161616] border border-[#262626] rounded-lg p-5" data-testid="sidepot-public-entrants">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs uppercase tracking-wider font-bold text-[#FACC15]">
                          Who&apos;s in &middot; {results.entrants.length} entered &middot; awaiting scores
                        </p>
                        <span className="text-[10px] text-[#9E9E9E]">
                          Updates live as riders enter
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                        {results.entrants.map((e, i) => (
                          <div
                            key={e.entry_id || i}
                            className="flex items-center justify-between px-2.5 py-1.5 bg-[#0A0A0A] border border-[#1A1A1A] rounded text-[11px] sm:text-xs"
                            data-testid={`sidepot-public-entrant-${e.entry_id}`}
                          >
                            <span className="text-white truncate">
                              <span className="font-mono text-[#555] mr-2">{i + 1}.</span>
                              {e.rider_name}
                              {e.horse_name && <span className="text-[#9E9E9E]"> / {e.horse_name}</span>}
                            </span>
                            {e.is_rollover && (
                              <span className="ml-2 text-[9px] font-bold text-[#FACC15] bg-[#FACC15]/15 border border-[#FACC15]/30 rounded px-1.5 py-0.5 uppercase tracking-wider flex-shrink-0">
                                Rollover
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-[#161616] border border-[#262626] rounded-lg p-12 text-center">
                      <Trophy size={40} className="text-[#262626] mx-auto mb-3" />
                      <p className="text-[#9E9E9E]">No results for this class{activeTab !== 'overall' ? ` in ${activeTab}` : ''}</p>
                    </div>
                  )
                ) : (
                  <div className="space-y-2">
                    {displayResults.map((result, index) => {
                      const isNT = result.status === 'NT' || result.final_time === null;
                      const isSCR = result.status === 'SCR';
                      const place = (isNT || isSCR) ? null : (result.place || (index + 1));
                      const isTied = result.tied || false;
                      // On the Overall tab, show each rider's DIVISION payout
                      // (looked up by their in-division place). On a division tab
                      // use the place directly.
                      let payout = null;
                      if (place) {
                        if (activeTab === 'overall') {
                          const rDiv = getDivisionFor(result);
                          const rDivPlace = getDivisionPlaceFor(result);
                          if (rDiv && rDivPlace) payout = getPayoutForPlace(rDivPlace, rDiv);
                        } else {
                          payout = getPayoutForPlace(place, activeTab);
                        }
                      }
                      return (
                        <div
                          key={index}
                          data-testid={`result-row-${index}`}
                          className={`bg-[#161616] border rounded-lg p-4 sm:p-5 ${
                            isSCR ? 'border-[#FF3B30]/20 opacity-60' :
                            isNT ? 'border-[#262626] opacity-70' :
                            place === 1 ? 'border-[#FFD700] bg-gradient-to-r from-[#FFD700]/5 to-transparent' :
                            place === 2 ? 'border-[#C0C0C0] bg-gradient-to-r from-[#C0C0C0]/5 to-transparent' :
                            place === 3 ? 'border-[#CD7F32] bg-gradient-to-r from-[#CD7F32]/5 to-transparent' :
                            'border-[#262626]'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 overflow-hidden">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                              <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center relative flex-shrink-0 ${
                                isSCR ? 'bg-[#FF3B30]/10' :
                                isNT ? 'bg-[#262626]' :
                                place === 1 ? 'bg-[#FFD700]/20' :
                                place === 2 ? 'bg-[#C0C0C0]/20' :
                                place === 3 ? 'bg-[#CD7F32]/20' :
                                'bg-[#0A0A0A]'
                              }`}>
                                <span className={`font-black ${
                                  isSCR ? 'text-[#FF3B30] text-sm sm:text-base' :
                                  isNT ? 'text-[#9E9E9E] text-sm sm:text-base' :
                                  place === 1 ? 'text-[#FFD700]' :
                                  place === 2 ? 'text-[#C0C0C0]' :
                                  place === 3 ? 'text-[#CD7F32]' :
                                  'text-[#9E9E9E]'
                                } ${activeTab === 'overall' && getDivisionFor(result) && getDivisionPlaceFor(result) ? 'text-[10px] sm:text-xs leading-tight' : 'text-sm sm:text-base'}`} style={{fontFamily: 'Outfit'}}>
                                  {isSCR ? 'SCR' : isNT ? 'NT'
                                    : (activeTab === 'overall' && getDivisionFor(result) && getDivisionPlaceFor(result))
                                      ? `${getDivisionFor(result)}-${getDivisionPlaceFor(result)}`
                                      : place}
                                </span>
                                {isTied && !isNT && !isSCR && (
                                  <span className="absolute -top-1.5 -right-1.5 bg-[#FF9500] text-black text-[9px] font-black rounded-full w-4 h-4 flex items-center justify-center" data-testid={`tie-badge-${index}`}>T</span>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm sm:text-base font-bold text-white truncate" style={{fontFamily: 'Outfit'}}>{result.rider_name}</p>
                                <div className="flex items-center gap-2 text-[#9E9E9E] text-[10px] sm:text-xs flex-wrap">
                                  {result.horse_name && <span className="italic truncate">{result.horse_name}</span>}
                                  {/* Rolled-to (R/O) badge — compact: just "R/O {targets}". */}
                                  {Array.isArray(result.rolled_to) && result.rolled_to.length > 0 && (() => {
                                    const labels = result.rolled_to.map(r => r.class_name || r.sidepot_name || 'Unknown').join(', ');
                                    const isSidepot = result.rolled_to.some(r => r.sidepot_id);
                                    return (
                                      <span
                                        className={`inline-flex items-center px-1 py-0 rounded text-[9px] font-bold normal-case leading-none flex-shrink-0 ${isSidepot ? 'bg-[#FACC15]/15 text-[#FACC15]' : 'bg-[#FF9500]/15 text-[#FF9500]'}`}
                                        data-testid={`public-rolled-to-${index}`}
                                        title={`Rolled to: ${labels}`}
                                      >
                                        R/O {labels}
                                      </span>
                                    );
                                  })()}
                                  {activeTab === 'overall' && hasDivisions && getDivisionFor(result) && (() => {
                                    const dc = getDivisionColors(getDivisionFor(result));
                                    return (
                                      <span
                                        className={`px-1.5 py-0.5 border ${dc.text} ${dc.bg} ${dc.border} rounded text-[9px] font-bold flex-shrink-0`}
                                        data-testid={`result-division-${index}`}
                                      >
                                        {getDivisionFor(result)}
                                      </span>
                                    );
                                  })()}
                                  {isTied && !isNT && !isSCR && <span className="text-[#FF9500] font-semibold flex-shrink-0">TIE</span>}
                                  {isSCR && <span className="text-[#FF3B30] font-semibold flex-shrink-0">Scratch</span>}
                                  {isNT && !isSCR && <span className="text-[#9E9E9E] font-semibold flex-shrink-0">No Time</span>}
                                  {!isNT && !isSCR && result.knocks > 0 && <span className="text-[#FF3B30] flex-shrink-0">{result.knocks}K</span>}
                                </div>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-base sm:text-lg font-black text-white" style={{fontFamily: 'JetBrains Mono'}}>
                                {isSCR ? 'SCR' : isNT ? 'NT' : result.final_time?.toFixed(3)}
                              </p>
                              {payout ? (
                                <p className="text-[#34C759] text-sm font-bold mt-0.5" data-testid={`result-payout-${index}`}>
                                  ${payout.toLocaleString(undefined, {minimumFractionDigits: 2})}
                                  {isTied && <span className="text-[#FF9500] text-xs font-normal ml-1">ea</span>}
                                </p>
                              ) : (
                                <p className="text-[#9E9E9E] text-xs">seconds</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="bg-[#161616] border-t border-[#262626] mt-12 py-6">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[#9E9E9E]">Powered by <span className="text-[#0096FF] font-semibold">Show Master</span></p>
        </div>
      </div>

      {/* Email Results Modal */}
      {emailModalOpen && selectedClass && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => !emailSending && setEmailModalOpen(false)} data-testid="email-modal">
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                Email Results — {selectedClass.name}{activeTab !== 'overall' ? ` (${activeTab})` : ''}
              </h3>
              <button onClick={() => setEmailModalOpen(false)} className="text-[#9E9E9E] hover:text-white" data-testid="email-modal-close">
                <X size={16} />
              </button>
            </div>

            <p className="text-xs text-[#9E9E9E] mb-3">Each email includes an HTML preview in the body plus CSV + PDF attachments.</p>

            <label className="block text-[10px] text-[#9E9E9E] mb-1 uppercase tracking-wider">Recipients (comma- or line-separated)</label>
            <textarea
              value={emailCustomText}
              onChange={(e) => setEmailCustomText(e.target.value)}
              placeholder="rider@example.com, sponsor@example.com"
              rows={5}
              className="w-full px-3 py-2 mb-4 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500] placeholder:text-[#555] resize-none"
              data-testid="email-custom-input"
              autoFocus
            />

            <div className="flex gap-2">
              <button
                onClick={() => setEmailModalOpen(false)}
                disabled={emailSending}
                className="flex-1 px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded text-xs text-[#9E9E9E] hover:text-white disabled:opacity-50"
                data-testid="email-cancel"
              >
                Cancel
              </button>
              <button
                onClick={sendEmailResults}
                disabled={emailSending}
                className="flex-1 px-3 py-2 bg-[#FF9500] rounded text-xs text-white font-semibold hover:bg-[#FF9500]/80 disabled:opacity-60"
                data-testid="email-send"
              >
                {emailSending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowResults;

// ============================================================================
// v262 — Public Averages / Target Race results views.
// ============================================================================

const AverageResultsView = ({ showId, showName, avg, results, loading, onClose }) => {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
  const pdfUrl = `${BACKEND}/api/shows/${showId}/averages/${avg.id}/results.pdf`;
  const rows = Array.isArray(results?.results) ? results.results : [];
  const selectedClasses = results?.selected_classes || [];
  const divType = results?.division_type || 'NONE';
  const isDivisional = ['2D', '3D', '4D'].includes(divType) && rows.length > 0;
  const totalPool = Number(results?.total_pool || 0);
  const canPrint = rows.length > 0;

  // Bucket rows by division for divisional averages.
  const byDivision = React.useMemo(() => {
    if (!isDivisional) return null;
    const order = results?.divisions || ['1D', '2D', '3D', '4D'];
    const map = {};
    rows.forEach(r => {
      const d = r.division || '—';
      if (!map[d]) map[d] = [];
      map[d].push(r);
    });
    return order.filter(d => (map[d] || []).length > 0).map(d => [d, map[d]]);
  }, [isDivisional, rows, results]);

  return (
    <div data-testid="results-average-view">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{fontFamily: 'Outfit'}} data-testid="results-average-name">
            {avg.name}
          </h2>
          <p className="text-xs text-[#9E9E9E] mt-0.5">
            Average across {selectedClasses.length} {selectedClasses.length === 1 ? 'class' : 'classes'}
            {isDivisional && <span> · <span className="text-[#AF52DE] font-semibold">{divType}</span> divisional</span>}
            {totalPool > 0 && <span> · Pool <span className="text-[#34C759] font-semibold">${totalPool.toLocaleString()}</span></span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => window.open(`${pdfUrl}?inline=1`, '_blank', 'noopener')}
            disabled={!canPrint}
            data-testid="results-average-print"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-xs font-semibold hover:text-white hover:border-[#AF52DE]/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          ><Printer size={14} weight="bold" /> Print / PDF</button>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-xs font-semibold hover:text-white transition-colors"
            data-testid="results-average-close"
          ><X size={14} weight="bold" /> Close</button>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center"><div className="shimmer h-8 w-8 rounded-full mx-auto"></div></div>
      ) : rows.length === 0 ? (
        <div className="bg-[#161616] border border-[#262626] rounded-lg p-12 text-center">
          <ChartLine size={40} className="text-[#262626] mx-auto mb-3" />
          <p className="text-[#9E9E9E]">No eligible riders yet — need a time in every selected class.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {(isDivisional ? byDivision : [['Overall', rows]]).map(([label, drows]) => (
            <div key={label} data-testid={`avg-division-${label.toLowerCase()}`}>
              {isDivisional && (
                <h3 className="text-sm font-bold text-[#AF52DE] mb-2 uppercase tracking-wider">
                  {label} <span className="text-[#9E9E9E] font-normal">· {drows.length} rider{drows.length === 1 ? '' : 's'}</span>
                </h3>
              )}
              <div className="overflow-x-auto bg-[#161616] border border-[#262626] rounded-lg">
                <table className="w-full text-sm min-w-[600px]">
                  <thead className="bg-[#0A0A0A] text-[#9E9E9E]">
                    <tr>
                      <th className="text-left px-3 py-2 w-14">Place</th>
                      <th className="text-left px-3 py-2">Rider / Horse</th>
                      {selectedClasses.map(cls => (
                        <th key={cls.id} className="text-right px-3 py-2 whitespace-nowrap">{cls.name}</th>
                      ))}
                      <th className="text-right px-3 py-2">Total</th>
                      <th className="text-right px-3 py-2">Avg</th>
                      {totalPool > 0 && <th className="text-right px-3 py-2">Payout</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {drows.map((r, i) => (
                      <tr key={`${r.rider_name}-${r.horse_name}-${i}`} className={i % 2 === 1 ? 'bg-[#0A0A0A]/60' : ''}>
                        <td className="px-3 py-2 font-bold text-white">
                          {r.tied ? `T-${r.place}` : r.place || '—'}
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-white">{r.rider_name}</div>
                          {r.horse_name && <div className="text-[#9E9E9E] text-xs">{r.horse_name}</div>}
                        </td>
                        {selectedClasses.map(cls => {
                          const t = (r.times || {})[cls.id];
                          return (
                            <td key={cls.id} className="px-3 py-2 text-right text-white" style={{fontFamily: 'JetBrains Mono'}}>
                              {typeof t === 'number' ? t.toFixed(3) : <span className="text-[#555]">—</span>}
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-right text-white" style={{fontFamily: 'JetBrains Mono'}}>
                          {typeof r.total_time === 'number' ? r.total_time.toFixed(3) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-[#AF52DE]" style={{fontFamily: 'JetBrains Mono'}}>
                          {typeof r.average_time === 'number' ? r.average_time.toFixed(3) : '—'}
                        </td>
                        {totalPool > 0 && (
                          <td className="px-3 py-2 text-right font-bold text-[#34C759]" style={{fontFamily: 'JetBrains Mono'}}>
                            {typeof r.payout === 'number' && r.payout > 0 ? `$${r.payout.toLocaleString(undefined, {minimumFractionDigits: 2})}` : '—'}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TargetResultsView = ({ showId, showName, target, results, loading, onClose }) => {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
  const pdfUrl = `${BACKEND}/api/shows/${showId}/targets/${target.id}/results.pdf`;
  const rows = Array.isArray(results?.results) ? results.results : [];
  const totalPool = Number(results?.total_pool || 0);
  const payoutType = results?.payout_type || 'FROM_ENTRIES';
  const showPayout = totalPool > 0 && payoutType !== 'PRIZES_ONLY';
  const canPrint = rows.length > 0;

  return (
    <div data-testid="results-target-view">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{fontFamily: 'Outfit'}} data-testid="results-target-name">
            {target.name}
          </h2>
          <p className="text-xs text-[#9E9E9E] mt-0.5">
            Smallest difference between closest 2 times wins
            {rows.length > 0 && <span> · {rows.length} qualified</span>}
            {payoutType === 'PRIZES_ONLY' && <span> · <span className="text-[#AF52DE] font-semibold">Prizes only</span></span>}
            {showPayout && <span> · Pool <span className="text-[#34C759] font-semibold">${totalPool.toLocaleString()}</span></span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => window.open(`${pdfUrl}?inline=1`, '_blank', 'noopener')}
            disabled={!canPrint}
            data-testid="results-target-print"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-xs font-semibold hover:text-white hover:border-[#0096FF]/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          ><Printer size={14} weight="bold" /> Print / PDF</button>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-xs font-semibold hover:text-white transition-colors"
            data-testid="results-target-close"
          ><X size={14} weight="bold" /> Close</button>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center"><div className="shimmer h-8 w-8 rounded-full mx-auto"></div></div>
      ) : rows.length === 0 ? (
        <div className="bg-[#161616] border border-[#262626] rounded-lg p-12 text-center">
          <Target size={40} className="text-[#262626] mx-auto mb-3" />
          <p className="text-[#9E9E9E]">No qualifiers yet — riders need two completed runs in the target categories.</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-[#161616] border border-[#262626] rounded-lg">
          <table className="w-full text-sm min-w-[520px]">
            <thead className="bg-[#0A0A0A] text-[#9E9E9E]">
              <tr>
                <th className="text-left px-3 py-2">Rider / Horse</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-right px-3 py-2">T1</th>
                <th className="text-right px-3 py-2">T2</th>
                <th className="text-right px-3 py-2">Diff</th>
                {showPayout && <th className="text-right px-3 py-2">Payout</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.rider_name}-${r.horse_name}-${i}`} className={i % 2 === 1 ? 'bg-[#0A0A0A]/60' : ''} data-testid={`target-row-${i}`}>
                  <td className="px-3 py-2">
                    <div className="text-white">{r.rider_name}</div>
                    {r.horse_name && <div className="text-[#9E9E9E] text-xs">{r.horse_name}</div>}
                  </td>
                  <td className="px-3 py-2 text-[#0096FF]">{r.category_name || '—'}</td>
                  <td className="px-3 py-2 text-right text-white" style={{fontFamily: 'JetBrains Mono'}}>
                    {typeof r.target_time_1 === 'number' ? r.target_time_1.toFixed(3) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-white" style={{fontFamily: 'JetBrains Mono'}}>
                    {typeof r.target_time_2 === 'number' ? r.target_time_2.toFixed(3) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-[#0096FF]" style={{fontFamily: 'JetBrains Mono'}}>
                    {typeof r.target_diff === 'number' ? r.target_diff.toFixed(3) : '—'}
                  </td>
                  {showPayout && (
                    <td className="px-3 py-2 text-right font-bold text-[#34C759]" style={{fontFamily: 'JetBrains Mono'}}>
                      {typeof r.payout === 'number' && r.payout > 0 ? `$${r.payout.toLocaleString(undefined, {minimumFractionDigits: 2})}` : '—'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
